import React, { useState, useEffect, useRef } from 'react';
import { Save, Loader2, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';

const UserManagementModal = ({ isOpen, onClose, onSave, user }) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const nameInputRef = useRef(null);

  const fetchEmpresas = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000/api'}/empresas`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setEmpresas(data);
      }
    } catch (error) {
      console.error('Erro ao buscar empresas:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (currentUser?.role !== 'admin') {
        fetchEmpresas();
      }
      if (user) {
        setFormData({ ...user, password: '' });
      } else {
        setFormData({
          name: '',
          email: '',
          password: '',
          role: 'usuario',
          empresa_id: currentUser?.role === 'admin' ? currentUser.empresa_id : ''
        });
      }
      // Focus the name input
      setTimeout(() => {
        if (nameInputRef.current) {
          nameInputRef.current.focus();
        }
      }, 100);
    }
  }, [user, isOpen, currentUser]);

  useEffect(() => {
    if (isOpen && user && empresas.length > 0 && currentUser?.role !== 'admin') {
      let empresaId = user.empresa_id;
      if (!empresaId && user.empresa) {
        const found = empresas.find(e => e.nome === user.empresa);
        empresaId = found ? found.id : '';
      }
      setFormData(prev => ({ ...prev, empresa_id: empresaId }));
    }
  }, [empresas, user, isOpen, currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!formData.name || !formData.email) throw new Error("Nome e Email são obrigatórios");
      if (!user && !formData.password) throw new Error("Senha é obrigatória para novos usuários");
      if (formData.password && formData.password.length < 8) throw new Error("Senha deve ter no mínimo 8 caracteres");

      await onSave(formData);
      onClose();
    } catch (error) {
      toast({
        title: "Erro de Validação",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-slate-900 border-white/20 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            {user ? 'Editar Usuário' : 'Novo Usuário'}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {user ? 'Atualize as informações do usuário.' : 'Preencha os dados para criar um novo usuário.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">Nome Completo</label>
            <input
              ref={nameInputRef}
              name="name"
              value={formData.name || ''}
              onChange={handleChange}
              className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-white focus:ring-2 focus:ring-purple-500 outline-none"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email || ''}
              onChange={handleChange}
              className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-white focus:ring-2 focus:ring-purple-500 outline-none"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">
              Senha {user && <span className="text-xs text-gray-500 font-normal">(Deixe em branco para manter)</span>}
            </label>
            <input
              type="password"
              name="password"
              value={formData.password || ''}
              onChange={handleChange}
              className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-white focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder={user ? "********" : "Mínimo 8 caracteres"}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-300">Função</label>
              <select
                name="role"
                value={formData.role || 'usuario'}
                onChange={handleChange}
                className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-white focus:ring-2 focus:ring-purple-500 outline-none"
              >
                <option value="usuario">Usuário</option>
                <option value="admin">Administrador</option>
                <option value="superadmin">Super Admin</option>
              </select>
            </div>
            {currentUser?.role !== 'admin' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Empresa</label>
                <select
                  name="empresa_id"
                  value={formData.empresa_id || ''}
                  onChange={handleChange}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-white focus:ring-2 focus:ring-purple-500 outline-none"
                >
                  <option value="">Selecione uma empresa</option>
                  {empresas.map((empresa) => (
                    <option key={empresa.id} value={empresa.id}>{empresa.nome}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="ghost" onClick={onClose} className="hover:bg-white/10 text-white">
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserManagementModal;